/**
 * Presentational sub-parts extracted from `ThreadPanel.tsx` so the parent
 * stays under the file-size ceiling. These are render-only: every piece of
 * state lives in `ThreadPanel`, which threads the derived values + callbacks
 * down. Behavior is identical to the inline JSX they replaced.
 */
import { messageScreenQueries } from "@/lib/queries";
import { Message } from "./Message";
import { ThreadQaHeader, ThreadReplyAcceptBar } from "./ThreadQaControls";
import { EmptyState } from "@/components/ui/empty-state";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import {
  X,
  Bell,
  BellSlash,
  Users,
  ArrowSquareOut,
} from "@/components/ui/icons";

/**
 * Parent + reply row types, derived from the `messageScreenQueries.threadComposite`
 * query request's `$return` metadata so these parts accept precisely what
 * `ThreadPanel` passes (deep `readonly` Zero shapes included). Deriving — rather
 * than re-declaring structurally — keeps the parts in lockstep with the query
 * as its `.related()` shape evolves. The reference is type-only; no query runs
 * in this module.
 */
type ThreadCompositeRow = NonNullable<
  ReturnType<typeof messageScreenQueries.threadComposite>["~"]["$return"]
>;

/** The thread root message row. */
export type ThreadMessage = ThreadCompositeRow;
/** A single thread reply row (element of the composite's `thread_replies`). */
export type ThreadReply = ThreadMessage["thread_replies"][number];

type UserMap = Map<
  string,
  { id: string; display_name: string; avatar_url?: string | null }
>;

/** The thread panel's top header bar: Q&A title/solved/curate controls, jump
 *  to parent, follower count, follow toggle, close. */
export function ThreadPanelHeader({
  parentMessageId,
  isPublicWs,
  threadTitle,
  isSolved,
  canCurate,
  acceptedAnswerId,
  onScrollToParent,
  followerCount,
  isFollowing,
  onToggleFollow,
  onClose,
}: {
  parentMessageId: string;
  isPublicWs: boolean;
  threadTitle: string | null;
  isSolved: boolean;
  canCurate: boolean;
  acceptedAnswerId: string | null;
  onScrollToParent?: () => void;
  followerCount: number;
  isFollowing: boolean;
  onToggleFollow: () => void;
  onClose: () => void;
}) {
  return (
    <div className="h-12 border-b border-border px-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <ThreadQaHeader
          rootMessageId={parentMessageId}
          title={isPublicWs ? threadTitle : null}
          solved={isSolved}
          canCurate={canCurate}
          canReopen={canCurate && !!acceptedAnswerId}
        />
        {onScrollToParent && (
          <ToolbarIconButton
            onClick={onScrollToParent}
            className="h-7 w-7"
            title="Jump to message in chat"
            aria-label="Jump to message in chat"
          >
            <ArrowSquareOut size={14} />
          </ToolbarIconButton>
        )}
        {followerCount > 0 && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Users size={12} />
            {followerCount}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <ToolbarIconButton
          onClick={onToggleFollow}
          title={isFollowing ? "Unfollow thread" : "Follow thread"}
          aria-label={isFollowing ? "Unfollow thread" : "Follow thread"}
          className="h-7 w-7"
        >
          {isFollowing ? (
            <Bell size={16} weight="Bold" className="text-info" />
          ) : (
            <BellSlash size={16} className="text-muted-foreground" />
          )}
        </ToolbarIconButton>
        <ToolbarIconButton
          onClick={onClose}
          aria-label="Close thread"
          className="h-7 w-7"
        >
          <X size={16} className="text-muted-foreground" />
        </ToolbarIconButton>
      </div>
    </div>
  );
}

/** The thread root message row, with the citation-pill hydration from the
 *  composite's nested `quoted_source` slice. */
export function ThreadParentMessage({
  parentMessage,
  sharedUserMap,
  workspaceMembers,
  channels,
  onUserClick,
  onChannelClick,
  onSendToShell,
  actionPillOpen,
  onActionPillOpenChange,
  senderIsFormerMember,
}: {
  parentMessage: ThreadMessage;
  sharedUserMap: UserMap;
  workspaceMembers: { id: string; display_name: string }[];
  channels?: { id: string; name: string }[];
  onUserClick?: (userId: string) => void;
  onChannelClick?: (channelId: string) => void;
  onSendToShell?: (messageId: string) => void;
  actionPillOpen: boolean;
  onActionPillOpenChange: (open: boolean) => void;
  senderIsFormerMember: boolean;
}) {
  return (
    <Message
      message={parentMessage}
      user={parentMessage.sender ?? sharedUserMap.get(parentMessage.user_id)}
      reactions={(parentMessage.reactions as never[]) ?? []}
      attachments={(parentMessage.attachments as never[]) ?? []}
      linkPreviews={(parentMessage.link_previews as never[]) ?? []}
      userMap={sharedUserMap}
      workspaceMembers={workspaceMembers}
      channels={channels}
      onUserClick={onUserClick}
      onChannelClick={onChannelClick}
      onSendToShell={onSendToShell}
      actionPillOpen={actionPillOpen}
      onActionPillOpenChange={onActionPillOpenChange}
      senderIsFormerMember={senderIsFormerMember}
      // When the thread parent is itself an inline reply, hydrate
      // the citation pill from the `byThreadComposite`'s nested
      // `quoted_source` slice. Avoids the "unknown / [message]"
      // fallback render that would otherwise show because
      // ThreadPanel has no per-channel `messageById` to look up
      // the cited source from.
      quotedSource={
        parentMessage.quoted_source
          ? {
              id: parentMessage.quoted_source.id,
              content: parentMessage.quoted_source.content,
              user_id: parentMessage.quoted_source.user_id,
            }
          : null
      }
      quotedSourceAuthorName={
        parentMessage.quoted_source?.sender?.display_name ??
        (parentMessage.quoted_source
          ? (sharedUserMap.get(parentMessage.quoted_source.user_id)
              ?.display_name ?? null)
          : null)
      }
      quotedSourceAuthorAvatarURL={
        parentMessage.quoted_source?.sender?.avatar_url ??
        (parentMessage.quoted_source
          ? (sharedUserMap.get(parentMessage.quoted_source.user_id)
              ?.avatar_url ?? null)
          : null)
      }
    />
  );
}

/** The thread reply list: an empty state, then each reply with its public-
 *  community accept/accepted bar above the message row. */
export function ThreadReplyList({
  orderedReplies,
  replyCount,
  isPublicWs,
  parentMessageId,
  acceptedAnswerId,
  canCurate,
  sharedUserMap,
  workspaceMembers,
  channels,
  onUserClick,
  onChannelClick,
  onSendToShell,
  highlightedReplyId,
  isMessageOpen,
  setMessageOpen,
  activeWorkspaceMemberIds,
}: {
  orderedReplies: readonly ThreadReply[];
  replyCount: number;
  isPublicWs: boolean;
  parentMessageId: string;
  acceptedAnswerId: string | null;
  canCurate: boolean;
  sharedUserMap: UserMap;
  workspaceMembers: { id: string; display_name: string }[];
  channels?: { id: string; name: string }[];
  onUserClick?: (userId: string) => void;
  onChannelClick?: (channelId: string) => void;
  onSendToShell?: (messageId: string) => void;
  highlightedReplyId: string | null;
  isMessageOpen: (id: string) => boolean;
  setMessageOpen: (id: string, open: boolean) => void;
  activeWorkspaceMemberIds: Set<string> | null;
}) {
  return (
    <>
      {replyCount === 0 && <EmptyState layout="inset" title="No replies yet" />}
      {orderedReplies.map((msg) => (
        <div key={msg.id}>
          {isPublicWs && (
            <ThreadReplyAcceptBar
              rootMessageId={parentMessageId}
              replyId={msg.id}
              isAccepted={msg.id === acceptedAnswerId}
              canCurate={canCurate}
            />
          )}
          <Message
            message={msg}
            user={msg.sender ?? sharedUserMap.get(msg.user_id) ?? null}
            reactions={(msg.reactions as never[]) ?? []}
            attachments={(msg.attachments as never[]) ?? []}
            linkPreviews={(msg.link_previews as never[]) ?? []}
            userMap={sharedUserMap}
            workspaceMembers={workspaceMembers}
            channels={channels}
            onUserClick={onUserClick}
            onChannelClick={onChannelClick}
            onSendToShell={onSendToShell}
            highlighted={msg.id === highlightedReplyId}
            actionPillOpen={isMessageOpen(msg.id)}
            onActionPillOpenChange={(open) => setMessageOpen(msg.id, open)}
            senderIsFormerMember={
              !!activeWorkspaceMemberIds &&
              !activeWorkspaceMemberIds.has(msg.user_id)
            }
          />
        </div>
      ))}
    </>
  );
}
